import { motion } from "framer-motion";
import { CalendarCheck2, CheckCircle2, FileText } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const STATS = [
    { value: "24", label: "Open Jobs", accent: "#00B8D9" },
    { value: "8", label: "In Progress", accent: "#0066FF" },
    { value: "3", label: "Rebooked", accent: "#A855F7" },
    { value: "12", label: "Completed", accent: "#0F172A" },
];

const JOBS = [
    { time: "08:30", type: "Installation", where: "Smith Residence", status: "En route", pill: "bg-[#00E5FF]/10 text-[#00B8D9]" },
    { time: "10:00", type: "Service", where: "Greenway School", status: "On site", pill: "bg-[#0066FF]/10 text-[#0066FF]" },
    { time: "13:00", type: "Maintenance", where: "Lakeside Holiday Park", status: "Scheduled", pill: "bg-[#0F172A]/[0.06] text-[#64748B]" },
    { time: "15:30", type: "Service", where: "Hillcrest Spa", status: "Scheduled", pill: "bg-[#0F172A]/[0.06] text-[#64748B]" },
];

const PHONE_JOBS = [
    { type: "Installation", where: "Smith Residence", status: "En route", dot: "#00E5FF" },
    { type: "Service", where: "Greenway School", status: "On site", dot: "#0066FF" },
    { type: "Maintenance", where: "Lakeside Holiday Park", status: "Scheduled", dot: "#94A3B8" },
];

const Chip = ({ icon: Icon, title, sub, className, delay, testid }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay, duration: 0.6, ease: EASE }}
        className={`absolute ${className}`}
        data-testid={testid}
    >
        <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay }}
            className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-[#0B1220]/90 px-3.5 py-2.5 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.6)] backdrop-blur-md"
        >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0066FF]/15">
                <Icon className="h-4 w-4 text-[#00E5FF]" strokeWidth={1.75} />
            </span>
            <span>
                <span className="block text-xs font-bold text-white">{title}</span>
                <span className="block text-[10px] font-semibold text-[#94A3B8]">{sub}</span>
            </span>
        </motion.div>
    </motion.div>
);

// Code-built product visual — illustrative concept UI, no fake screenshots.
export default function HeroVisual() {
    return (
        <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: EASE }}
            className="relative hidden lg:block"
            data-testid="hero-visual"
        >
            {/* dashboard browser card */}
            <div
                className="overflow-hidden rounded-2xl border border-[#0F172A]/10 bg-white shadow-[0_40px_90px_-30px_rgba(0,0,0,0.35)]"
                data-testid="hero-visual-dashboard"
            >
                <div className="flex items-center gap-2 border-b border-[#0F172A]/[0.06] bg-[#F7F8FA] px-4 py-2.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#0F172A]/10" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#0F172A]/10" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#0F172A]/10" />
                    <span className="ml-3 rounded-md bg-white px-3 py-1 font-mono-brand text-[9px] tracking-wide text-[#94A3B8] ring-1 ring-[#0F172A]/[0.06]">
                        app.tedopsai.com/dashboard
                    </span>
                </div>
                <div className="grid grid-cols-[52px_1fr_148px]">
                    <div className="flex flex-col items-center gap-3 bg-[#0B1220] py-4">
                        <span className="h-7 w-7 rounded-lg bg-gradient-to-br from-[#00E5FF] via-[#0066FF] to-[#A855F7]" />
                        {["#00E5FF", "#475569", "#475569", "#475569", "#475569"].map((c, i) => (
                            <span key={i} className="h-6 w-6 rounded-md" style={{ background: `${c}33` }} />
                        ))}
                    </div>
                    <div className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-display text-sm font-extrabold text-[#0F172A]">Good morning, Ian</p>
                                <p className="text-[10px] font-medium text-[#94A3B8]">Here's what's happening today.</p>
                            </div>
                            <span className="rounded-full bg-[#F0F7FF] px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-[#0066FF]">
                                Live
                            </span>
                        </div>
                        <div className="mt-3 grid grid-cols-4 gap-2">
                            {STATS.map((s) => (
                                <div key={s.label} className="rounded-lg border border-[#0F172A]/[0.06] bg-[#F7F8FA] px-2 py-2 text-center">
                                    <p className="font-display text-base font-extrabold text-[#0F172A]">{s.value}</p>
                                    <p className="mt-0.5 text-[8px] font-bold uppercase tracking-wider" style={{ color: s.accent }}>
                                        {s.label}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <p className="mt-3 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Today's jobs</p>
                        <div className="mt-1.5 space-y-1">
                            {JOBS.map((j) => (
                                <div key={j.time} className="flex items-center gap-2.5 rounded-lg border border-[#0F172A]/[0.05] px-2.5 py-1.5">
                                    <span className="font-mono-brand text-[9px] font-semibold text-[#64748B]">{j.time}</span>
                                    <span className="text-[11px] font-bold text-[#0F172A]">{j.type}</span>
                                    <span className="min-w-0 flex-1 truncate text-[10px] text-[#94A3B8]">{j.where}</span>
                                    <span className={`rounded-full px-2 py-0.5 text-[8px] font-bold uppercase tracking-wider ${j.pill}`}>
                                        {j.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* live map column — the phone overlaps this zone */}
                    <div className="relative border-l border-[#0F172A]/[0.06] bg-[#EEF3FA]">
                        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 148 300" preserveAspectRatio="none" aria-hidden="true">
                            <path d="M0 60 H148 M0 140 H148 M0 220 H148 M40 0 V300 M96 0 V300" stroke="#CBD5E1" strokeWidth="6" />
                            <path d="M0 100 H148 M68 0 V300" stroke="#E2E8F0" strokeWidth="3" />
                            <path d="M20 260 C 60 200, 50 150, 96 90 S 130 40, 140 20" stroke="#0066FF" strokeWidth="2.5" strokeDasharray="5 5" fill="none" />
                        </svg>
                        <span className="absolute left-[60px] top-[120px] h-2.5 w-2.5 rounded-full bg-[#00E5FF] ring-2 ring-white" />
                        <span className="absolute left-[92px] top-[86px] h-2.5 w-2.5 rounded-full bg-[#0066FF] ring-2 ring-white" />
                        <span className="absolute left-[26px] top-[236px] h-2.5 w-2.5 rounded-full bg-[#A855F7] ring-2 ring-white" />
                        <span className="absolute left-2 top-2 rounded-md bg-[#0B1220]/85 px-2 py-1 text-[8px] font-bold uppercase tracking-wider text-white backdrop-blur-sm">
                            Engineers live 5/12
                        </span>
                    </div>
                </div>
            </div>

            {/* overlapping phone */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75, duration: 0.8, ease: EASE }}
                className="absolute -bottom-9 -right-5 w-[150px] overflow-hidden rounded-[1.6rem] border border-[#0F172A]/10 bg-white shadow-[0_30px_70px_-20px_rgba(0,0,0,0.35)]"
                data-testid="hero-visual-phone"
            >
                <div className="bg-[#0B1220] px-3.5 pb-2.5 pt-3">
                    <p className="font-display text-[10px] font-extrabold text-white">Today's Jobs</p>
                    <p className="text-[7.5px] font-medium text-[#94A3B8]">3 scheduled · 1 en route</p>
                </div>
                <div className="space-y-1.5 p-2">
                    {PHONE_JOBS.map((j) => (
                        <div key={j.where} className="rounded-lg border border-[#0F172A]/[0.06] p-2">
                            <div className="flex items-center gap-1.5">
                                <span className="h-1.5 w-1.5 rounded-full" style={{ background: j.dot }} />
                                <p className="text-[9px] font-bold text-[#0F172A]">{j.type}</p>
                                <span className="ml-auto text-[6.5px] font-bold uppercase tracking-wider text-[#94A3B8]">{j.status}</span>
                            </div>
                            <p className="mt-1 truncate text-[8px] text-[#94A3B8]">{j.where}</p>
                        </div>
                    ))}
                </div>
                <div className="flex justify-around border-t border-[#0F172A]/[0.06] py-2">
                    <span className="h-1 w-7 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#A855F7]" />
                    <span className="h-1 w-7 rounded-full bg-[#0F172A]/10" />
                    <span className="h-1 w-7 rounded-full bg-[#0F172A]/10" />
                </div>
            </motion.div>

            {/* floating chips */}
            <Chip
                icon={CalendarCheck2}
                title="Jobs scheduled"
                sub="+32% this month"
                className="-top-6 left-8"
                delay={0.95}
                testid="hero-visual-chip-1"
            />
            <Chip
                icon={FileText}
                title="Invoice created"
                sub="Sent to customer"
                className="-left-10 top-[46%]"
                delay={1.15}
                testid="hero-visual-chip-2"
            />
            <Chip
                icon={CheckCircle2}
                title="Job completed"
                sub="Photos & forms sent"
                className="-right-3 top-16"
                delay={1.35}
                testid="hero-visual-chip-3"
            />
        </motion.div>
    );
}
